<!--
  MovedLessonTeacherDialog: "Lehrkraft klären" for a moved lesson that crosses a teacher change (feat/moved-lesson-teacher).
  "Am 22.10. ist Herr B für den Kurs zuständig, die ursprüngliche Stunde hatte Frau A. Wer hält die Stunde?"
  Choices: ursprüngliche Lehrkraft / Lehrkraft am neuen Termin (preselected) / andere Lehrkraft. Saved as teacher override of the
  move (PATCH lesson-moves/:id/teacher-decision); refused (409) once the original week is billed.
  Props: value, event (moved lesson, lessonMove.role 'moved') – or, outside of the calendar (to-do "Lehrkraft klären"),
  move ({_id, course, courseCustomId, originalDate, newStart}, see movedLessonTeacherToDo.helper). Rights: course update of
  `teacher` (checked by the caller and the backend).
  Events: input (false = close), decided after saving (the calendar is reloaded via TimetableBus).
-->
<template>
  <cal-dialog-shell :initializing="initializing" :loading="loading" :save-disabled="!canSave" :subtitle="subtitle" :value="value"
                    icon="mdi-account-question" max-width="560" persistent save-icon="mdi-check" save-text="Lehrkraft festlegen"
                    title="Lehrkraft klären" tone="warn" @input="close" @save="save">
    <section v-if="crossing" class="mdlg-section">
      <div class="cdlg-box tone-muted">{{ lessonText }}</div>
      <div class="cdlg-box tone-warn">
        <v-icon small style="color: inherit">mdi-alert-outline</v-icon> {{ question }}
      </div>
      <div class="mdlg-field soft-inputs">
        <span class="acq-label">Wer hält die Stunde?<span class="mdlg-req"> *</span></span>
        <div class="cdlg-pills" role="radiogroup" aria-label="Wer hält die Stunde?">
          <button v-for="option in options" :key="option.value" :aria-checked="String(choice === option.value)"
                  :class="{ 'pill--active': choice === option.value }" class="pill" role="radio" type="button"
                  @click="choice = option.value">{{ option.text }}</button>
        </div>
        <employee-autocomplete v-if="choice === 'other'" v-model="otherTeacher" :filter="{ isTeacher: true }" color="info" dense
                               disable-applicants hide-details label="" outlined placeholder="Lehrkraft suchen"/>
        <div class="acq-tiny">Abgerechnet wird die Stunde bei der gewählten Lehrkraft (für die Woche der ursprünglichen Stunde).</div>
      </div>
    </section>
  </cal-dialog-shell>
</template>

<script lang="ts">
import Vue from 'vue';
import moment from 'moment';
import {Types} from 'mongoose';
import CalDialogShell from '@/components/timetable2/dialogs/CalDialogShell.vue';
import EmployeeAutocomplete from '@/components/common/search-fields/employeeAutocomplete.vue';
import {courseSubtitle} from '@/components/timetable2/dialogs/calDialog.helper';
import {CourseManagerAPI} from '@/classes/api/course-manager.api.class';
import {LessonMoveTeacherCheckResponse} from '@/classes/dto/course-manager/request/lessonMove/LessonMoveTeacherCheck.request.dto';
import {TimetableBus} from '@/busses/TimetableBus';
import {CourseTimetableItem} from '@/helpers/timetable2/types';
import {idOf} from '@/helpers/timetable2/occurrenceModel';
import {backendMessage, moveInfoOf} from '@/helpers/timetable2/lessonMove';
import {
  crossingChoiceOf,
  crossingOptions,
  crossingQuestion,
  CrossingChoice,
  crossingTeacherOf,
} from '@/helpers/timetable2/movedLessonTeacher';
import {MovedLessonTeacherMove} from '@/components/toDos/movedLessonTeacherToDo.helper';

export default Vue.extend({
  components: {CalDialogShell, EmployeeAutocomplete},
  props: {
    value: {type: Boolean, required: true},
    /** Moved lesson in the calendar */
    event: {type: Object as () => CourseTimetableItem | null, default: null},
    /** Without calendar event (to-do): the lesson move itself */
    move: {type: Object as () => MovedLessonTeacherMove | null, default: null},
  },
  data: () => ({
    initializing: false,
    loading: false,
    crossing: null as LessonMoveTeacherCheckResponse | null,
    choice: 'new' as CrossingChoice,
    otherTeacher: null as Types.ObjectId | null,
  }),
  computed: {
    info(): { _id: unknown; originalDate: Date | string; newStart: Date | string } | undefined {
      return this.event ? moveInfoOf(this.event) : this.move || undefined;
    },
    /** Course segment for the teacher check */
    courseId(): string | undefined {
      return this.event ? idOf(this.event.data?._id || this.event._id) : this.move?.course;
    },
    subtitle(): string {
      if (this.event) {
        return courseSubtitle(this.event);
      }
      return this.move ? [this.move.courseCustomId, moment(this.move.newStart).format('dd, DD.MM. HH:mm')].filter(Boolean).join(' · ') : '';
    },
    lessonText(): string {
      const info = this.info;
      return info ? `Stunde vom ${moment(info.originalDate).format('dd, DD.MM.YYYY')} verschoben auf ${moment(info.newStart).format('dd, DD.MM.YYYY, HH:mm')} Uhr` : '';
    },
    question(): string {
      return this.crossing && this.info ? crossingQuestion(this.crossing, this.info.newStart) : '';
    },
    options(): Array<{ value: CrossingChoice; text: string }> {
      return this.crossing ? crossingOptions(this.crossing) : [];
    },
    teacher(): Types.ObjectId | null | undefined {
      return this.crossing ? crossingTeacherOf(this.crossing, this.choice, this.otherTeacher) : undefined;
    },
    canSave(): boolean {
      return !!this.crossing && this.teacher !== undefined;
    },
  },
  methods: {
    close() {
      if (!this.loading) {
        this.$emit('input', false);
      }
    },
    async initialize() {
      const info = this.info;
      if (!info || !this.courseId) {
        this.$emit('input', false);
        return;
      }
      this.initializing = true;
      this.crossing = null;
      this.otherTeacher = null;
      try {
        const [check, moves] = await Promise.all([
          CourseManagerAPI.lessonMoveTeacherCheck({
            course: new Types.ObjectId(this.courseId),
            originalDate: moment(info.originalDate).toDate(),
            newStart: moment(info.newStart).toDate(),
          }),
          CourseManagerAPI.findLessonMoves({filter: {_id: [info._id as Types.ObjectId]}}),
        ]);
        this.crossing = check;
        const preset = crossingChoiceOf(check, moves?.lessonMoves?.[0]);
        this.choice = preset.choice;
        this.otherTeacher = preset.teacherId ?? null;
      } catch (e) {
        this.$$showSnackbar(backendMessage(e) || 'Die Lehrkräfte der verschobenen Stunde konnten nicht geladen werden', 'error');
        this.$emit('input', false);
      } finally {
        this.initializing = false;
      }
    },
    async save() {
      const info = this.info;
      if (!this.canSave || !info) {
        return;
      }
      this.loading = true;
      try {
        await CourseManagerAPI.decideLessonMoveTeacher(info._id as Types.ObjectId, {teacher: this.teacher ?? null});
        this.$$showSnackbar('Die Lehrkraft der verschobenen Stunde wurde festgelegt', 'success');
        TimetableBus.$emit('reload:event');
        TimetableBus.$emit('update:events');
        this.$emit('decided');
        this.$emit('input', false);
      } catch (e) {
        this.$$showSnackbar(backendMessage(e) || 'Die Lehrkraft konnte nicht festgelegt werden', 'error');
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(open: boolean) {
        if (open) {
          this.initialize();
        }
      },
    },
  },
});
</script>
